import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { useQuery } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { Link, Loader2 } from 'lucide-react'
import {
  EventActivityPreviousSetup,
  getEventActivityPreviousSetups,
} from 'app/frontend/base/api/event'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useDefaultSetups } from 'app/frontend/base/config/queries'
import dayjs from 'dayjs'
import { ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useTeamEventUpdateContext } from '../context/TeamEventUpdateContext'
import { useTeamEventParticipantsContext } from '../context/TeamEventParticipantsContext'
import { toast } from 'sonner'
import LinkButton from '@/components/ui/link-button'
import PreviousEventMenuItem from '../../TeamPlans/components/PreviousEventMenuItem'
import { useDifficulties } from 'app/frontend/base/hooks/useDifficulties'
import Difficulty, {
  difficultiesLabels,
} from 'app/frontend/types/custom/Difficulty'
import {
  raidSizeLabel,
  raidSizeOptions,
} from 'app/frontend/base/helpers/raidSize'
import {
  buildDefaultSetupEntries,
  matchesDefaultSetup,
} from '../../TeamPlans/components/default_setup/defaultSetupEntries'

type Props = {
  activity: wowaudit.EventActivity
  activities: wowaudit.EventActivity[]
  allSelected: boolean
  trigger: ReactNode
  extraItems?: ReactNode
}

const EventActivityHeaderContextMenu = ({
  activity,
  activities,
  allSelected,
  trigger,
  extraItems,
}: Props) => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const { difficulties: availableDifficulties, hasDifficulties } =
    useDifficulties()
  const { event } = useTeamEventParticipantsContext()
  const { eventId } = useParams()
  const {
    updateActivitySelectionForAllParticipants,
    updateEventActivity,
    copySelectionsFromActivity,
    importSelectionsFromDefaultSetup,
    removeEventActivity,
    isPreviewActive = false,
  } = useTeamEventUpdateContext()
  const numericEventId = Number(eventId)
  const [isOpen, setIsOpen] = useState(false)
  const [copyingToActivityId, setCopyingToActivityId] = useState<number | null>(
    null,
  )
  const [importingFromActivityId, setImportingFromActivityId] = useState<
    number | null
  >(null)
  const [isImportingDefaultSetup, setIsImportingDefaultSetup] = useState(false)
  const [isUpdatingActivity, setIsUpdatingActivity] = useState(false)
  const [isRemovingActivity, setIsRemovingActivity] = useState(false)
  const [updatingDifficulty, setUpdatingDifficulty] =
    useState<Difficulty | null>(null)
  const [updatingRaidSize, setUpdatingRaidSize] = useState<number | null>(null)
  const copyTargetActivities = useMemo(
    () => activities.filter((candidate) => candidate.id !== activity.id),
    [activities, activity.id],
  )
  const { data: previousSetupsResponse, isFetching: isLoadingPreviousSetups } =
    useQuery({
      queryKey: [
        'eventActivityPreviousSetups',
        {
          teamId: team.id,
          eventId: numericEventId,
          activityId: activity.id,
        },
      ],
      enabled: isOpen && Number.isFinite(numericEventId),
      staleTime: 60_000,
      queryFn: async () =>
        await getEventActivityPreviousSetups(
          team.id,
          numericEventId,
          activity.id,
        ),
    })
  const previousSetups = previousSetupsResponse?.previousSetups ?? []
  const { data: defaultSetups } = useDefaultSetups(team.id)
  const showDefaultSetupRow =
    activity.activityType === 'raids' && !!activity.encounter
  const matchingDefaultSetup = useMemo(() => {
    const encounterId = activity.encounter?.id
    if (!encounterId) return undefined
    return (defaultSetups ?? []).find((setup) =>
      matchesDefaultSetup(setup, encounterId, activity.difficulty),
    )
  }, [activity.difficulty, activity.encounter, defaultSetups])
  const defaultSetupSelectedCount = useMemo(() => {
    if (!showDefaultSetupRow) return 0
    return buildDefaultSetupEntries({
      team,
      members: team.members ?? [],
      activityId: activity.id,
      selections: matchingDefaultSetup?.selections,
    }).filter((entry) => entry.selected).length
  }, [activity.id, matchingDefaultSetup?.selections, showDefaultSetupRow, team])
  const hasPendingSelectionCopy =
    !!copyingToActivityId ||
    !!importingFromActivityId ||
    isImportingDefaultSetup
  const hasPendingAction =
    hasPendingSelectionCopy ||
    isUpdatingActivity ||
    isRemovingActivity ||
    !!updatingDifficulty ||
    updatingRaidSize !== null
  const canRemoveActivity = activities.length > 1

  const handleCopySelections = async (targetActivityId: number) => {
    setCopyingToActivityId(targetActivityId)
    try {
      await copySelectionsFromActivity(targetActivityId, activity.id)
    } finally {
      setCopyingToActivityId(null)
    }
  }
  const handleImportSelections = async (setup: EventActivityPreviousSetup) => {
    setImportingFromActivityId(setup.activityId)
    try {
      await copySelectionsFromActivity(
        activity.id,
        setup.activityId,
        setup.event.id,
      )

      toast.success('Selections imported successfully')
    } finally {
      setImportingFromActivityId(null)
      setIsOpen(false)
    }
  }
  const handleImportDefaultSetup = async () => {
    if (!matchingDefaultSetup) return
    setIsImportingDefaultSetup(true)
    try {
      await importSelectionsFromDefaultSetup(activity.id)
      toast.success('Selections imported successfully')
    } finally {
      setIsImportingDefaultSetup(false)
      setIsOpen(false)
    }
  }
  const handleChangeDifficulty = async (difficulty: Difficulty) => {
    if (difficulty === activity.difficulty) return
    setUpdatingDifficulty(difficulty)
    try {
      await updateEventActivity(activity.id, { difficulty })
      setIsOpen(false)
    } finally {
      setUpdatingDifficulty(null)
    }
  }
  const handleChangeRaidSize = async (raidSize: number) => {
    if (raidSize === activity.raidSize) return
    setUpdatingRaidSize(raidSize)
    try {
      await updateEventActivity(activity.id, { raidSize })
      setIsOpen(false)
    } finally {
      setUpdatingRaidSize(null)
    }
  }
  const handleUpdateActivity = async () => {
    setIsUpdatingActivity(true)
    try {
      await updateEventActivity(activity.id, { active: !activity.active })
      setIsOpen(false)
    } finally {
      setIsUpdatingActivity(false)
    }
  }
  const handleRemoveActivity = async () => {
    setIsRemovingActivity(true)
    try {
      await removeEventActivity(activity.id)
      setIsOpen(false)
    } finally {
      setIsRemovingActivity(false)
    }
  }

  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (hasPendingAction) return
        setIsOpen(nextOpen)
      }}
    >
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align='end'
        onCloseAutoFocus={(event) => {
          event.preventDefault()
        }}
      >
        <DropdownMenuLabel>
          <div className='flex items-center gap-2'>
            {activity.encounter?.thumbnailUrl ||
            activity.instance?.thumbnailUrl ||
            activity.instance?.tileUrl ? (
              <img
                className='h-8 w-8 rounded-md border border-border shrink-0'
                src={
                  activity.encounter?.thumbnailUrl ??
                  activity.instance?.thumbnailUrl ??
                  activity.instance?.tileUrl ??
                  ''
                }
              />
            ) : (
              <div className='h-8 w-8 rounded-md border border-border bg-muted shrink-0' />
            )}

            <div className='min-w-0'>
              <div className='truncate'>
                {activity.encounter?.name || activity.instance?.name || '-'}
              </div>
              {activity.encounter || hasDifficulties ? (
                <div className='text-muted-foreground text-xs font-normal'>
                  {(activity.encounter
                    ? activity.instance?.name
                    : difficultiesLabels[activity.difficulty]) || '-'}
                </div>
              ) : null}
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {activity.activityType === 'raids' && hasDifficulties ? (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              disabled={hasPendingAction || isPreviewActive}
            >
              <span className='flex min-w-0 flex-1 items-center justify-between gap-4'>
                <span>Difficulty</span>
                <span className='text-muted-foreground text-xs'>
                  {difficultiesLabels[activity.difficulty]}
                </span>
              </span>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                {availableDifficulties.map((difficulty) => {
                  const isCurrent = difficulty === activity.difficulty
                  const isUpdating = updatingDifficulty === difficulty
                  return (
                    <DropdownMenuItem
                      key={difficulty}
                      disabled={
                        hasPendingAction || isCurrent || isPreviewActive
                      }
                      onSelect={(event) => {
                        event.preventDefault()
                        if (hasPendingAction || isCurrent || isPreviewActive)
                          return
                        void handleChangeDifficulty(difficulty)
                      }}
                    >
                      <span className='flex w-full items-center justify-between gap-2'>
                        {difficultiesLabels[difficulty]}
                        {isUpdating ? (
                          <Loader2 className='h-3.5 w-3.5 animate-spin text-muted-foreground' />
                        ) : null}
                      </span>
                    </DropdownMenuItem>
                  )
                })}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        ) : null}
        {activity.instance?.altRaidSize ? (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              disabled={hasPendingAction || isPreviewActive}
            >
              <span className='flex min-w-0 flex-1 items-center justify-between gap-4'>
                <span>Raid size</span>
                <span className='text-muted-foreground text-xs'>
                  {raidSizeLabel(activity.raidSize)}
                </span>
              </span>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                {raidSizeOptions(activity.instance).map((option) => {
                  const raidSize = Number(option.value)
                  const isCurrent = raidSize === activity.raidSize
                  const isUpdating = updatingRaidSize === raidSize
                  return (
                    <DropdownMenuItem
                      key={option.value}
                      disabled={
                        hasPendingAction || isCurrent || isPreviewActive
                      }
                      onSelect={(event) => {
                        event.preventDefault()
                        if (hasPendingAction || isCurrent || isPreviewActive)
                          return
                        void handleChangeRaidSize(raidSize)
                      }}
                    >
                      <span className='flex w-full items-center justify-between gap-2'>
                        {option.label}
                        {isUpdating ? (
                          <Loader2 className='h-3.5 w-3.5 animate-spin text-muted-foreground' />
                        ) : null}
                      </span>
                    </DropdownMenuItem>
                  )
                })}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        ) : null}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            disabled={
              copyTargetActivities.length === 0 ||
              hasPendingAction ||
              !activity.active ||
              isPreviewActive
            }
          >
            Copy setup to
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              {copyTargetActivities.map((targetActivity) => {
                const isCopying = copyingToActivityId === targetActivity.id
                return (
                  <DropdownMenuItem
                    key={targetActivity.id}
                    disabled={hasPendingAction || !targetActivity.active}
                    onSelect={(event) => {
                      event.preventDefault()
                      if (hasPendingAction) return
                      void handleCopySelections(targetActivity.id)
                    }}
                  >
                    <span className='flex w-full items-center gap-2'>
                      {isCopying ? (
                        <Loader2 className='h-8 w-8 animate-spin shrink-0 text-muted-foreground' />
                      ) : targetActivity.encounter?.thumbnailUrl ||
                        targetActivity.instance?.thumbnailUrl ||
                        targetActivity.instance?.tileUrl ? (
                        <img
                          className={cn(
                            'h-8 w-8 rounded border border-border shrink-0',
                            !targetActivity.active && 'grayscale',
                          )}
                          src={
                            targetActivity.encounter?.thumbnailUrl ??
                            targetActivity.instance?.thumbnailUrl ??
                            targetActivity.instance?.tileUrl ??
                            ''
                          }
                        />
                      ) : (
                        <span
                          className={cn(
                            'h-8 w-8 rounded border border-border bg-muted shrink-0',
                            !targetActivity.active && 'grayscale',
                          )}
                        />
                      )}
                      <span className='min-w-0 flex flex-col'>
                        <span className='truncate'>
                          {targetActivity.encounter?.name ||
                            targetActivity.instance?.name ||
                            '-'}
                        </span>
                        {targetActivity.encounter || hasDifficulties ? (
                          <span className='text-xs text-muted-foreground truncate'>
                            {targetActivity.encounter
                              ? `${targetActivity.instance?.name}${
                                  hasDifficulties ? ', ' : ''
                                }`
                              : null}
                            {hasDifficulties
                              ? difficultiesLabels[targetActivity.difficulty]
                              : null}
                          </span>
                        ) : null}
                      </span>
                    </span>
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            disabled={hasPendingAction || !activity.active || isPreviewActive}
          >
            Import setup from
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              {showDefaultSetupRow ? (
                <DropdownMenuItem
                  disabled={hasPendingAction}
                  onSelect={(selectEvent) => {
                    selectEvent.preventDefault()
                    if (hasPendingAction || !matchingDefaultSetup) return
                    void handleImportDefaultSetup()
                  }}
                >
                  <span className='flex w-full items-center gap-2'>
                    {isImportingDefaultSetup ? (
                      <Loader2 className='h-8 w-8 animate-spin shrink-0 text-muted-foreground' />
                    ) : activity.encounter?.thumbnailUrl ? (
                      <img
                        className='h-8 w-8 rounded-md border border-border shrink-0'
                        src={activity.encounter.thumbnailUrl}
                      />
                    ) : (
                      <div className='h-8 w-8 rounded-md border border-border bg-muted shrink-0' />
                    )}
                    <span className='min-w-0 flex flex-col w-full'>
                      <span className='flex items-center gap-1.5 min-w-0'>
                        {matchingDefaultSetup?.enabled ? null : (
                          <Badge
                            variant='warning'
                            className='shrink-0 px-1.5 py-0'
                          >
                            {t(
                              'dashboard.team.plans.default-setup.inactive-label',
                            )}
                          </Badge>
                        )}
                        <span className='truncate'>
                          {t('dashboard.team.plans.default-setup.title')}
                        </span>
                      </span>
                      <div className='flex items-center justify-between gap-2 w-full'>
                        <span className='text-xs text-muted-foreground'>
                          {defaultSetupSelectedCount} selected
                        </span>

                        <LinkButton
                          to={`${team.path}/events/default-setups`}
                          target='_blank'
                          rel='noreferrer'
                          size='xs'
                          onClick={(event) => {
                            event.stopPropagation()
                          }}
                          onMouseDown={(event) => {
                            event.stopPropagation()
                          }}
                        >
                          <Link size={12} />
                        </LinkButton>
                      </div>
                    </span>
                  </span>
                </DropdownMenuItem>
              ) : null}
              {isLoadingPreviousSetups ? (
                <DropdownMenuItem disabled>
                  <span className='flex items-center gap-2'>
                    <Loader2 className='h-4 w-4 animate-spin shrink-0 text-muted-foreground' />
                    Loading setups
                  </span>
                </DropdownMenuItem>
              ) : previousSetups.length === 0 ? (
                showDefaultSetupRow ? null : (
                  <DropdownMenuItem disabled>
                    No previous setups
                  </DropdownMenuItem>
                )
              ) : (
                previousSetups.map((setup) => (
                  <PreviousEventMenuItem
                    key={`${setup.event.id}:${setup.activityId}`}
                    thumbnailUrl={activity.encounter?.thumbnailUrl}
                    title={dayjs(setup.event.date).format('ddd, MMM D YYYY')}
                    detail={`${setup.totalParticipants} selected`}
                    href={`${team.path}/events/${setup.event.id}`}
                    pending={importingFromActivityId === setup.activityId}
                    disabled={hasPendingAction}
                    onSelect={(event) => {
                      event.preventDefault()
                      if (hasPendingAction) return
                      void handleImportSelections(setup)
                    }}
                  />
                ))
              )}
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          disabled={!activity.active || hasPendingAction}
          onClick={() => {
            updateActivitySelectionForAllParticipants(activity.id, !allSelected)
          }}
        >
          {allSelected ? 'Bench all participants' : 'Select all participants'}
        </DropdownMenuItem>
        {event.raidsPlanningScope === 'single' ? null : (
          <DropdownMenuItem
            disabled={hasPendingAction}
            onSelect={(selectEvent) => {
              selectEvent.preventDefault()
              if (hasPendingAction) return
              void handleUpdateActivity()
            }}
          >
            <div className='flex items-center justify-between gap-2 w-full'>
              {activity.active ? 'Set inactive' : 'Set active'}
              {isUpdatingActivity ? (
                <Loader2 className='h-3.5 w-3.5 animate-spin text-muted-foreground' />
              ) : null}
            </div>
          </DropdownMenuItem>
        )}

        {extraItems ? (
          <>
            <DropdownMenuSeparator />
            {extraItems}
          </>
        ) : null}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          className='text-destructive-foreground'
          disabled={!canRemoveActivity || hasPendingAction || isPreviewActive}
          onSelect={(event) => {
            event.preventDefault()
            if (!canRemoveActivity || hasPendingAction || isPreviewActive)
              return
            void handleRemoveActivity()
          }}
        >
          <div className='flex items-center justify-between gap-2 w-full'>
            Remove
            {isRemovingActivity ? (
              <Loader2 className='h-3.5 w-3.5 animate-spin text-muted-foreground' />
            ) : null}
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default EventActivityHeaderContextMenu
